import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Sparkles, Edit3, ArrowLeft } from "lucide-react";
import { money } from "../../lib/api";

/**
 * Persistent inline quick-add. Type to search the pricing library.
 * Press Enter to add the highlighted item. Up/Down to navigate.
 * Visually emphasised when the quote is empty — glow + helper hint —
 * so first-time staff don't have to think "how do I add products".
 */
export default function QuickAddRow({ rules, onAddRule, onAddBlank, attentionMode = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Stop the attention treatment once the user has engaged at all.
  const showAttention = attentionMode && !hasInteracted;

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const matches = useMemo(() => {
    if (!query) return [];
    const s = query.toLowerCase();
    return rules
      .filter((r) =>
        r.item_name.toLowerCase().includes(s) ||
        (r.description || "").toLowerCase().includes(s) ||
        (r.supplier || "").toLowerCase().includes(s) ||
        r.category.toLowerCase().includes(s)
      )
      .slice(0, 8);
  }, [rules, query]);

  // Cmd/Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleKey = (e) => {
    setHasInteracted(true);
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, matches.length)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlight < matches.length && matches[highlight]) onAddRule(matches[highlight]);
      else if (query.trim()) onAddBlank(query.trim());
      setQuery(""); setHighlight(0);
    }
    if (e.key === "Escape") { setQuery(""); setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Helper label that points at the bar when empty */}
      {showAttention && (
        <div
          className="flex items-center gap-2 mb-2 text-xs font-bold tracking-widest uppercase"
          style={{ color: "var(--accent)" }}
          data-testid="quick-add-helper-hint"
        >
          <ArrowLeft className="w-3 h-3" style={{ transform: "rotate(-30deg)" }} />
          Start here to add line items
        </div>
      )}

      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${showAttention ? "pq-pulse" : ""}`}
        style={{
          background: showAttention ? "var(--gradient-accent-soft)" : "var(--surface-2)",
          border: open
            ? "1px solid var(--accent)"
            : showAttention
              ? "1px solid var(--accent)"
              : "1px solid var(--border-strong)",
          boxShadow: showAttention ? "0 0 0 4px rgba(255, 61, 138, 0.10)" : "none",
        }}
      >
        <Sparkles className="w-5 h-5 shrink-0" style={{ color: "var(--accent)" }} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); setHasInteracted(true); }}
          onFocus={() => { setOpen(true); setHasInteracted(true); }}
          onKeyDown={handleKey}
          placeholder={attentionMode ? "Start typing products / materials…  (e.g. vinyl, ACM, banner)" : "Add another item — type to search…"}
          data-testid="quick-add-input"
          className="flex-1 bg-transparent outline-none text-sm"
          style={{
            color: "var(--text-primary)",
            fontSize: showAttention ? "0.95rem" : "0.875rem",
          }}
        />
        <div className="hidden md:flex items-center gap-1 text-[10px] font-mono" style={{ color: "var(--text-faint)" }}>
          <kbd className="px-1.5 py-0.5 rounded" style={{ background: "var(--surface-1)", border: "1px solid var(--border-subtle)" }}>⌘K</kbd>
          <span>to focus</span>
        </div>
        <button
          onClick={() => { setHasInteracted(true); onAddBlank(""); }}
          className="btn btn-ghost btn-sm"
          data-testid="quick-add-blank-btn"
          title="Add a blank line"
        >
          <Edit3 className="w-3.5 h-3.5" /> Blank
        </button>
      </div>

      {open && query && (
        <div
          className="absolute left-0 right-0 mt-2 surface overflow-hidden shadow-2xl z-30"
          style={{ background: "var(--surface-2)" }}
          data-testid="quick-add-results"
        >
          {matches.length === 0 && (
            <div className="px-4 py-4 text-sm" style={{ color: "var(--text-muted)" }}>
              No matches.{" "}
              <button
                onClick={() => { onAddBlank(query); setQuery(""); }}
                className="font-bold tracking-wider uppercase text-xs"
                style={{ color: "var(--accent)" }}
              >
                Add "{query}" as custom line →
              </button>
            </div>
          )}
          {matches.map((r, i) => (
            <button
              key={r.id}
              onClick={() => { onAddRule(r); setQuery(""); }}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`quick-add-result-${r.id}`}
              className="w-full px-4 py-2.5 flex items-center justify-between gap-4 transition-colors text-left"
              style={{ background: highlight === i ? "var(--surface-3)" : "transparent" }}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
                  {r.item_name}
                </div>
                <div className="text-[11px] font-mono truncate" style={{ color: "var(--text-faint)" }}>
                  {r.category} · {r.unit}{r.supplier ? ` · ${r.supplier}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm" style={{ color: "var(--text-primary)" }} data-num>
                  {money(r.sell)}
                </div>
                <div className="text-[10px] font-mono" style={{ color: "var(--text-faint)" }}>per {r.unit}</div>
              </div>
            </button>
          ))}
          {matches.length > 0 && (
            <button
              onClick={() => { onAddBlank(query); setQuery(""); }}
              onMouseEnter={() => setHighlight(matches.length)}
              data-testid="quick-add-blank-fallback"
              className="w-full px-4 py-2.5 flex items-center gap-2 text-left border-t transition-colors"
              style={{
                borderColor: "var(--border-subtle)",
                color: "var(--text-secondary)",
                background: highlight === matches.length ? "var(--surface-3)" : "transparent",
              }}
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="text-xs font-bold tracking-wider uppercase">Add "{query}" as custom line</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
